/**
 * Storage abstraction for Outlook add-in.
 *
 * On Windows New Outlook, localStorage is undefined in event-based activation handlers
 * (OfficeDev/office-js#1977). OfficeRuntime.Storage is available instead.
 *
 * This module uses localStorage when available (OWA, Mac), and falls back to
 * OfficeRuntime.Storage when localStorage is undefined (Windows event handler).
 */

import { config } from './config';

/** OfficeRuntime.Storage interface — available in Outlook event-based activation on Windows. */
interface OfficeRuntimeStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

declare global {
  interface Window {
    OfficeRuntime?: { storage?: OfficeRuntimeStorage };
  }
}

function hasLocalStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage !== null;
  } catch {
    return false;
  }
}

function hasOfficeRuntimeStorage(): boolean {
  try {
    const g = globalThis as unknown as { OfficeRuntime?: { storage?: OfficeRuntimeStorage } };
    return typeof globalThis !== 'undefined' && g.OfficeRuntime?.storage != null;
  } catch {
    return false;
  }
}

/**
 * Get a value from storage. Prefers localStorage; falls back to OfficeRuntime.Storage on Windows.
 */
export async function getItem(key: string): Promise<string | null> {
  if (hasLocalStorage()) {
    try {
      return localStorage.getItem(key);
    } catch {
      // Fall through to OfficeRuntime
    }
  }
  if (hasOfficeRuntimeStorage()) {
    try {
      const storage = (globalThis as unknown as { OfficeRuntime: { storage: OfficeRuntimeStorage } }).OfficeRuntime.storage;
      return await storage.getItem(key);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Set a value in storage. Prefers localStorage; falls back to OfficeRuntime.Storage on Windows.
 */
export async function setItem(key: string, value: string): Promise<void> {
  if (hasLocalStorage()) {
    try {
      localStorage.setItem(key, value);
      return;
    } catch {
      // Fall through to OfficeRuntime
    }
  }
  if (hasOfficeRuntimeStorage()) {
    try {
      const storage = (globalThis as unknown as { OfficeRuntime: { storage: OfficeRuntimeStorage } }).OfficeRuntime.storage;
      await storage.setItem(key, value);
    } catch {
      // Ignore storage errors
    }
  }
}

/**
 * Remove a value from storage.
 */
export async function removeItem(key: string): Promise<void> {
  if (hasLocalStorage()) {
    try {
      localStorage.removeItem(key);
      return;
    } catch {
      // Fall through
    }
  }
  if (hasOfficeRuntimeStorage()) {
    try {
      const storage = (globalThis as unknown as { OfficeRuntime: { storage: OfficeRuntimeStorage } }).OfficeRuntime.storage;
      await storage.removeItem(key);
    } catch {
      // Ignore
    }
  }
}

/** Storage keys from config. */
export const storageKeys = config.storageKeys;
